import { ChevronRight, FileText } from "lucide-react";

import type { EncounterListItem } from "@elation/api-client/src/stitch-web/models";

import { EncounterEntity } from "@src/features/encounters";
import {
  Flexbox,
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Badge } from "@src/shared/components/ui/badge";
import { Button } from "@src/shared/components/ui/button";
import { Card } from "@src/shared/components/ui/card";

export interface PatientPreviousVisitsV2Props {
  encounters: EncounterListItem[];
  onEncounterSelect?: (encounterId: number) => void;
}

function PatientPreviousVisitRow({
  encounter,
  onSelect,
}: {
  encounter: EncounterListItem;
  onSelect?: (encounterId: number) => void;
}) {
  const metadata = EncounterEntity.patientVisitMetadata(encounter);
  const notePreview = EncounterEntity.notePreview(encounter);

  return (
    <li aria-label={notePreview ?? `Visit ${encounter.id}`}>
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-start gap-3 whitespace-normal rounded-none border-b border-border bg-transparent px-4 py-3 text-left font-normal shadow-none hover:bg-muted/40 hover:text-foreground focus-visible:ring-1 last:border-b-0"
        onClick={() => onSelect?.(encounter.id)}
      >
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="size-9 shrink-0 rounded-full bg-primary/5 text-primary"
        >
          <FileText className="size-4" aria-hidden="true" />
        </Flexbox>
        <VerticalFlexbox className="min-w-0 flex-1">
          <HorizontalFlexbox
            alignItems="center"
            flexWrap="wrap"
            className="gap-x-2 gap-y-1"
          >
            <span className="truncate text-sm font-semibold text-surface-foreground">
              {notePreview || "Past visit"}
            </span>
            <Badge variant="outline" className="font-mono text-[10px]">
              {encounter.status}
            </Badge>
          </HorizontalFlexbox>
          {metadata ? (
            <p className="mt-1 font-mono text-[10.5px] text-muted-foreground">
              {metadata}
            </p>
          ) : null}
        </VerticalFlexbox>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Button>
    </li>
  );
}

export function PatientPreviousVisitsV2({
  encounters,
  onEncounterSelect,
}: PatientPreviousVisitsV2Props) {
  if (encounters.length === 0) return null;

  return (
    <section aria-labelledby="patient-detail-previous-visits-heading">
      <VerticalFlexbox className="gap-2">
        <h2
          id="patient-detail-previous-visits-heading"
          className="font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground"
        >
          Previous visits
        </h2>
        <Card className="overflow-hidden rounded-[10px] bg-surface shadow-none">
          <ul
            aria-labelledby="patient-detail-previous-visits-heading"
            className="divide-y-0"
          >
            {encounters.map((encounter) => (
              <PatientPreviousVisitRow
                key={encounter.id}
                encounter={encounter}
                onSelect={onEncounterSelect}
              />
            ))}
          </ul>
        </Card>
      </VerticalFlexbox>
    </section>
  );
}
